import useApp from 'hooks/useApp';
import useData from 'hooks/useData';
import { memo, useMemo } from 'react';
import { useNestedTranslation } from 'hooks/useNestedTranslation';
import { convertColour } from 'utils/canvas';
import { LoadingWrapper } from 'components/LoadingIndicator/LoadingWrapper';
import ingkaColours from '@ingka/variables/colours-css';
import { getAllBudgetHours, getCapacityHours } from 'hooks/useGrid/gridFunctionsShared';
import ingkaSizes from '@ingka/variables/design-tokens';
import { toIntlLongMonthFullYear } from 'utils/date';
import moment from 'moment';
import { calculateSum, isNegative, roundDisplayValueDoubleDecimal } from 'utils/number';
import type { TFunction } from 'i18next';
import type { ConfigurationData } from 'types/api';
import Graph from './Graph';
import GraphHeader from './GraphHeader';
import { ChartDataWithLabel, ChartGeneratorOptions, GraphData } from './types';

const generateGraphOptions = (
    t: TFunction<'translation', undefined, 'translation'>,
    graphData: GraphData,
    config: ConfigurationData | undefined,
    labelBudget: string,
    labelMaxCapacity: string
): ChartGeneratorOptions => (
    {
        layout: {
            padleft: graphData.datasets.length && graphData.datasets[0].data.length ? 0 : 20,
        },
        scales: {
            tickColor: convertColour(ingkaColours.colourStaticBlack),
            weeksThreshold: 16,
            labels: graphData.labels,
            hasNegativeValues: graphData.datasets.some(ds => ds.data.some(d => d < 0)),
            formatterYTicks: roundDisplayValueDoubleDecimal
        },
        plugins: {
            customYLabel: {
                text: t('hours'),
                color: convertColour(ingkaColours.colourStaticBlack),
                offsetX: graphData.datasets.length && graphData.datasets[0].data.length ? 0 : 20,
            },
            emptyGraph: {
                text: t('no_data')
            },
            tooltip: {
                internal: {
                    titleFormatter: (currentTitle: string) => (
                        `${t('week')} ${currentTitle.slice(-2)} (${toIntlLongMonthFullYear(moment(currentTitle).toDate())})`
                    ),
                    backgroundColor: convertColour(ingkaColours.colourNeutral7),
                    numberFormatter: roundDisplayValueDoubleDecimal,
                    differenceOptions: config?.isContractRangeSupported ? {
                        differenceFormatter: (gap: number) => (
                            `${t('gap')}: ${isNegative(gap)
                                ? roundDisplayValueDoubleDecimal(gap)
                                : `+${roundDisplayValueDoubleDecimal(gap)}`}`
                        ),
                        labelMinuend: labelMaxCapacity,
                        labelSubtrahend: labelBudget,
                    } : undefined,
                }
            }
        }

    }
);

const emptyDataset = (t: TFunction<'translation', undefined, 'translation'>) => [{
    label: t('needed_capacity'),
    data: [],
}, {
    label: t('available_capacity'),
    data: [],
}];

export const CapacityOverviewGraph = memo(() => {
    const t = useNestedTranslation('graph');
    const { gridData, timeSelection } = useApp();
    const { config, currentScenario, isFetching } = useData();

    const labelBudget = useMemo(() => t('needed_capacity'), [t]);
    const labelMaxCapacity = useMemo(() => {
        const suffix = config?.isContractRangeSupported ? '_max_short' : '';
        if (currentScenario) {
            return t(`scenario_capacity${suffix}`);
        }

        return t(`available_capacity${suffix}`);
    }, [config?.isContractRangeSupported, currentScenario, t]);

    const graphData = useMemo<GraphData>(() => {
        if (!gridData || !config) {
            return {
                datasets: emptyDataset(t),
                labels: timeSelection.timeArray,
            };
        }
        const capacityHours = gridData.hoverData.map(d => getCapacityHours(d.gap));
        const budgetHours = getAllBudgetHours(gridData.hoverData);

        const newDataSets: ChartDataWithLabel[] = [{
            type: 'line',
            label: labelBudget,
            data: budgetHours,
            backgroundColor: '#0061B4',
            spanGaps: true,
            segment: {
                borderColor: '#0061B4',
                backgroundColor: '#0061B4',
                borderWidth: 2, // px
            },
            pointRadius: 0, // px
            borderColor: '#0061B4',
        }];

        if (config.isContractRangeSupported) {
            const scenarioHoursMin = gridData.hoverData.map(d => getCapacityHours(d.minRangeGap ?? d.gap));
            newDataSets.push(
                {
                    type: 'bar',
                    label: currentScenario ? t('scenario_capacity_min_short') : t('available_capacity_min_short'),
                    data: scenarioHoursMin,
                    backgroundColor: convertColour(ingkaColours.colourNeutral5),
                    barThickness: 8, // px
                    hoverBackgroundColor: convertColour(ingkaColours.colourNeutral7),
                    borderColor: convertColour(ingkaColours.colourNeutral5),
                }
            );
        }

        newDataSets.push(
            {
                type: 'bar',
                label: labelMaxCapacity,
                data: capacityHours,
                backgroundColor: convertColour(ingkaColours.colourNeutral4),
                barThickness: 8, // px
                hoverBackgroundColor: convertColour(ingkaColours.colourNeutral5),
            },
        );

        return {
            datasets: newDataSets,
            labels: timeSelection.timeArray
        };
    }, [gridData, config, currentScenario, t, timeSelection.timeArray, labelBudget, labelMaxCapacity]);

    return (
        <LoadingWrapper isLoading={isFetching}>
            <>
                <GraphHeader
                    headerTitle={t('target_and_scenario_title')}
                    headerSubTitle={t('target_and_scenario_subtitle')}
                    legends={graphData.datasets.map(el => ({
                        label: el.label,
                        value: calculateSum(el.data),
                        color: el.backgroundColor as string
                    }))}
                />
                <Graph
                    graphData={graphData}
                    graphOptions={generateGraphOptions(t.global, graphData, config, labelBudget, labelMaxCapacity)}
                    customOptions={{ height: ingkaSizes.space1350 }}
                />
            </>
        </LoadingWrapper>
    );
});
